import { graphql, useStaticQuery } from "gatsby";
import { FAQGroup, Question } from "./types";
import { DEFAULT_CATEGORY_NAME } from "./constants";
import { getSortingNumber } from "./helpers";

export const useGroupedFaqLibrary = (
  lang: string,
  isApp = false,
  isMainPage = false,
  questionIds?: string[],
) => {
  const { questionsPool, faqCategoriesData } = useStaticQuery(graphql`
    {
      questionsPool: allStoryblokEntry(
        filter: { field_component: { eq: "faqLibraryItem" } }
      ) {
        edges {
          node {
            content
            full_slug
            uuid
            lang
          }
        }
      }
      faqCategoriesData: allStoryblokDatasourceEntry(
        filter: { data_source: { eq: "faq-categories" } }
      ) {
        edges {
          node {
            id: value
            dimensionValue: dimension_value
            dimensionName: data_source_dimension
          }
        }
      }
    }
  `);

  const localizedQuestionsPool = questionsPool.edges
    .filter((faqItemNode) =>
      questionIds?.length
        ? questionIds.includes(faqItemNode.node.uuid) &&
          faqItemNode.node.lang === lang
        : faqItemNode.node.lang === lang,
    )
    ?.map((faqItemNode) => ({
      uuid: faqItemNode.node.uuid,
      content: faqItemNode.node.content,
      fullSlug: faqItemNode.node.full_slug,
    }));

  const sortedQuestions = questionIds?.length
    ? questionIds
        .map(
          (questionId) =>
            localizedQuestionsPool.filter(
              (question) => question.uuid === questionId,
            )[0],
        )
        .filter((question) => question)
    : localizedQuestionsPool;

  const groupedQuestions: FAQGroup[] = sortedQuestions.reduce(
    (groups: FAQGroup[], question: Question) => {
      const match = question.fullSlug.match(
        /(?:\w+\/)?faq-library(?:\/([\w-]+\/))?/,
      );
      const parsedQuestion = JSON.parse(question.content);

      if (match) {
        const groupId = match[1]?.replace("/", "") || DEFAULT_CATEGORY_NAME;

        const group = groups.filter((group) => group.id === groupId)[0];

        return group
          ? [
              ...groups.filter((group) => group.id !== groupId),
              {
                ...group,
                data:
                  (isApp && parsedQuestion.isHiddenInApp) ||
                  (isMainPage && parsedQuestion.isHiddenOnMainPage)
                    ? group.data
                    : [...group.data, parsedQuestion],
              },
            ]
          : [
              ...groups,
              {
                id: groupId,
                name: faqCategoriesData.edges.filter(
                  (faqItem) =>
                    faqItem.node.id === groupId &&
                    faqItem.node.dimensionName === lang,
                )[0]?.node.dimensionValue,
                sortingOrder: getSortingNumber(faqCategoriesData, groupId),
                data:
                  (isApp && parsedQuestion.isHiddenInApp) ||
                  (isMainPage && parsedQuestion.isHiddenOnMainPage)
                    ? []
                    : [parsedQuestion],
              },
            ];
      }

      return groups;
    },
    [],
  );

  return groupedQuestions.sort(
    (groupA, groupB) => groupA.sortingOrder - groupB.sortingOrder,
  );
};
